import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, HeartHandshake, CheckCircle2, Clock, XCircle, Zap, Sparkles } from "lucide-react";

export default function Donation() {
  const [info, setInfo] = useState(null);
  const [mine, setMine] = useState(null);
  const [form, setForm] = useState({ sender_name: "", amount: 50000, transfer_date: new Date().toISOString().slice(0,10), reference_note: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [i, m] = await Promise.all([http.get("/donations/info"), http.get("/donations/mine")]);
    setInfo(i.data); setMine(m.data);
    setForm(f => ({ ...f, amount: Math.max(f.amount, i.data.min_amount) }));
  };
  useEffect(() => { load(); }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Disalin: " + text); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.amount < (info?.min_amount || 50000)) { toast.error("Nominal kurang dari minimum"); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/donations", form);
      if (data.status === "approved") toast.success("Donasi otomatis disetujui! Akses jurnal terbuka.");
      else toast.success("Terima kasih! Bukti donasi terkirim, menunggu verifikasi admin.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim"); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const StatusBadge = ({ status }) => {
    if (status === "approved") return <Badge className="bg-emerald-600"><CheckCircle2 size={12} className="mr-1"/>Disetujui</Badge>;
    if (status === "rejected") return <Badge className="bg-red-600"><XCircle size={12} className="mr-1"/>Ditolak</Badge>;
    return <Badge className="bg-amber-500"><Clock size={12} className="mr-1"/>Menunggu review</Badge>;
  };

  return (
    <div data-testid="donation-page">
      <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">Dukungan</p>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1">Buka Akses Direktori Jurnal</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Direktori 65+ jurnal Scopus &amp; SINTA + tombol submit langsung tersedia bagi pendukung. Donasi minimal <b className="text-[#D97706]">Rp{(info?.min_amount || 50000).toLocaleString("id-ID")}</b>.</p>
        </div>
        {info?.auto_approve && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold shadow-sm">
            <Zap size={16} className="fill-emerald-500 text-emerald-600" /> Verifikasi Instan Aktif
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        {/* Bank card */}
        <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-[#0F2922] via-[#173E34] to-[#0F2922] text-[#FAFA96] shadow-xl">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#D97706]/20 blur-3xl"/>
          <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl"/>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <HeartHandshake />
              <span className="text-xs uppercase tracking-widest opacity-80">Transfer ke</span>
            </div>
            <div className="text-3xl serif font-bold">{info?.bank_name || "BCA"}</div>
            <div className="mt-6 flex items-center gap-3">
              <div className="text-3xl mono font-bold" data-testid="donation-rekening">
                {(info?.account_number || "8855003551").replace(/(\d{4})(\d{4})(\d+)/, "$1  $2  $3")}
              </div>
              <Button size="icon" variant="secondary" className="h-9 w-9 bg-[#FAFA96]/20 hover:bg-[#FAFA96]/30 text-[#FAFA96]" onClick={() => copy(info?.account_number || "8855003551")} data-testid="copy-rekening-btn"><Copy size={14}/></Button>
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Atas Nama</div>
              <div className="text-xl font-semibold">{info?.account_holder || "kawantugas.id"}</div>
            </div>
            <div className="mt-5 pt-5 border-t border-[#FAFA96]/20 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">Nominal Minimal</div>
                <div className="text-2xl font-bold text-[#D97706]">Rp {(info?.min_amount || 50000).toLocaleString("id-ID")}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest opacity-70">Mata Uang</div>
                <div className="font-semibold">IDR</div>
              </div>
            </div>
          </div>
        </div>

        {/* Form / status */}
        <div className="kt-card p-6">
          {mine?.donation ? (
            <div>
              <h2 className="serif text-xl font-bold text-[#0F2922]">Status Donasi Anda</h2>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-2">Status: <StatusBadge status={mine.donation.status} />
                  {mine.donation.auto_approved && <Badge variant="outline" className="border-emerald-500 text-emerald-700"><Zap size={10} className="mr-1"/>Instan</Badge>}
                </div>
                <div>Pengirim: <b>{mine.donation.sender_name}</b></div>
                <div>Nominal: <b className="text-[#D97706]">Rp {Number(mine.donation.amount).toLocaleString("id-ID")}</b></div>
                <div>Tgl transfer: {mine.donation.transfer_date}</div>
                {mine.donation.reference_note && <div>Catatan: {mine.donation.reference_note}</div>}
              </div>
              {mine.has_access && (
                <Button className="mt-4 w-full bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" onClick={() => nav("/direktori-jurnal")} data-testid="donation-goto-journals"><Sparkles size={16} className="mr-2"/>Buka Direktori Jurnal</Button>
              )}
              {mine.donation.status === "rejected" && (
                <Button variant="outline" className="mt-4 w-full" onClick={() => setMine({ ...mine, donation: null })}>Kirim Ulang</Button>
              )}
            </div>
          ) : (
            <form onSubmit={submit}>
              <h2 className="serif text-xl font-bold text-[#0F2922]">Konfirmasi Donasi</h2>
              <p className="text-xs text-slate-500 mt-1">{info?.auto_approve ? "Mode instan aktif — donasi Anda langsung diproses." : "Admin akan memverifikasi < 24 jam."}</p>
              <div className="mt-4 space-y-3">
                <div><Label>Nama Pengirim (sesuai transfer)</Label>
                  <Input required value={form.sender_name} onChange={e=>set("sender_name", e.target.value)} data-testid="donation-sender-input" />
                </div>
                <div><Label>Nominal (Rp)</Label>
                  <Input required type="number" min={info?.min_amount || 50000} step={1000} value={form.amount} onChange={e=>set("amount", parseInt(e.target.value || "0"))} data-testid="donation-amount-input" />
                </div>
                <div><Label>Tanggal transfer</Label>
                  <Input required type="date" value={form.transfer_date} onChange={e=>set("transfer_date", e.target.value)} data-testid="donation-date-input" />
                </div>
                <div><Label>Catatan / berita transfer (opsional)</Label>
                  <Textarea value={form.reference_note} onChange={e=>set("reference_note", e.target.value)} data-testid="donation-note-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="donation-submit-btn">
                  {busy ? "Mengirim…" : (info?.auto_approve ? "Kirim & Buka Akses Instan" : "Kirim Konfirmasi Donasi")}
                </Button>
                <p className="text-xs text-slate-500 mt-2">Informasi Anda hanya dipakai untuk verifikasi transfer.</p>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="mt-8 kt-card p-6">
        <h3 className="font-semibold text-[#0F2922]">Langkah-langkah</h3>
        <ol className="mt-3 grid md:grid-cols-4 gap-4 text-sm">
          {[
            ["1", "Transfer", `Kirim min. Rp${(info?.min_amount||50000).toLocaleString("id-ID")} ke ${info?.bank_name || "BCA"} ${info?.account_number || "8855003551"}`],
            ["2", "Konfirmasi", "Isi form konfirmasi di atas dengan nama pengirim & nominal"],
            ["3", "Verifikasi", info?.auto_approve ? "Instan otomatis diproses sistem" : "Admin verifikasi < 24 jam"],
            ["4", "Akses", "Direktori jurnal Scopus & SINTA terbuka penuh"],
          ].map(([n, t, d]) => (
            <li key={n} className="p-4 rounded-xl border border-slate-200 bg-[#FAFA96]/40">
              <div className="w-8 h-8 rounded-full bg-[#0F2922] text-[#FAFA96] flex items-center justify-center font-bold text-sm">{n}</div>
              <div className="mt-3 font-semibold text-[#0F2922]">{t}</div>
              <div className="text-xs text-slate-600 mt-1">{d}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
